import type { AutoClaimedKeyHuntDto, KeyHuntDto, KeyHuntRewardDto, KeyHuntTaskDto } from "../api/dto/key_hunt.dto.js";
import { featureDateTime, logRewardCoins, rewardsLabel, trackFeatureEvent } from "../analytics/feature_events.js";

function eventId( hunt: Pick<KeyHuntDto, "period_start"> ): string
{
  return `key_hunt@${hunt.period_start}`;
}

function common( hunt: KeyHuntDto ): Record<string, unknown>
{
  return {
    event_id: eventId( hunt ),
    config_version: hunt.version,
    date_start: featureDateTime( hunt.period_start ),
    date_end: featureDateTime( hunt.period_end ),
    tasks_total: hunt.tasks.length,
    tasks_completed: hunt.tasks.filter( task => task.completed ).length,
    keys_count: hunt.keys,
    keys_needed: hunt.keys_needed,
    prize_index: hunt.chosen_prize ?? -1,
  };
}

function taskParams( task: KeyHuntTaskDto ): Record<string, unknown>
{
  return {
    task_id: task.id,
    task_metric: task.metric,
    task_param: task.param ?? "",
    task_target: task.target,
    task_progress: task.progress,
    rewards: rewardsLabel( [ task.reward ] ),
  };
}

export const KeyHuntAnalytics = {
  observe( hunt: KeyHuntDto | null | undefined ): void
  {
    if ( !hunt || hunt.tasks.length === 0 ) return;
    const id = eventId( hunt );
    trackFeatureEvent( "key_hunt_started", common( hunt ), id );
    for ( const task of hunt.tasks )
    {
      if ( task.completed ) trackFeatureEvent( "key_hunt_task_completed", { ...common( hunt ), ...taskParams( task ) }, `${id}|${task.id}` );
    }
    if ( hunt.chosen_prize !== null ) this.prizeChosen( hunt );
  },

  taskClaimed( hunt: KeyHuntDto, task: KeyHuntTaskDto ): void
  {
    const sent = trackFeatureEvent( "key_hunt_task_claimed", { ...common( hunt ), ...taskParams( task ), auto: false }, `${eventId( hunt )}|${task.id}` );
    if ( sent ) logRewardCoins( [ task.reward ], "key_hunt_task" );
  },

  prizeChosen( hunt: KeyHuntDto ): void
  {
    const prize = hunt.chosen_prize === null ? null : hunt.prize_options[hunt.chosen_prize];
    if ( !prize ) return;
    trackFeatureEvent( "key_hunt_prize_chosen", { ...common( hunt ), rewards: rewardsLabel( [ prize ] ) }, eventId( hunt ) );
  },

  prizeClaimed( hunt: KeyHuntDto, rewards: KeyHuntRewardDto[] ): void
  {
    const sent = trackFeatureEvent( "key_hunt_prize_claimed", { ...common( hunt ), rewards: rewardsLabel( rewards ), auto: false }, eventId( hunt ) );
    if ( sent ) logRewardCoins( rewards, "key_hunt_prize" );
  },

  autoClaimed( claimed: AutoClaimedKeyHuntDto[] ): void
  {
    for ( const item of claimed )
    {
      const id = eventId( item );
      let sent = false;
      for ( const task_id of item.task_ids )
        sent = trackFeatureEvent( "key_hunt_task_claimed", { event_id: id, task_id, auto: true }, `${id}|${task_id}` ) || sent;
      if ( item.prize )
        sent = trackFeatureEvent( "key_hunt_prize_claimed", { event_id: id, prize_index: item.prize_index ?? -1, rewards: rewardsLabel( [ item.prize ] ), auto: true }, id ) || sent;
      if ( sent ) logRewardCoins( item.rewards, "key_hunt_auto" );
    }
  },

  opened( hunt: KeyHuntDto | null | undefined ): void
  {
    if ( !hunt ) return;
    trackFeatureEvent( "key_hunt_opened", {
      ...common( hunt ),
      claimable_count: hunt.tasks.filter( task => task.claimable ).length + ( hunt.prize_claimable ? 1 : 0 ),
    } );
  },
};
